import commonStyles from "../../../styles/common.module.css";
import ErrorIcon from "@mui/icons-material/Error";
import { CardContent } from "@mui/material";
import { useTranslation } from "react-i18next";

type Props = {
  onExit: () => void;
  onRetry: () => void;
  onContinue: () => void;
  description: string;
  header: string;
  body: string;
};

/**
 * Shown on a desktop capture when the barcode on the back of the document could
 * not be read.
 *
 * An unread barcode is not a reason to stop. The engine has still read the visual
 * zone, the reviewer checks the details either way, and nothing on the server side
 * refuses a submission for it — the check is advisory, returned alongside a 200.
 * This screen used to offer only Retake and Exit, though, so an applicant whose
 * barcode would not read on a webcam had no way forward at all: retake until it
 * works, or abandon the journey. Continuing is now one of the three, and the
 * mobile hand-off is a link rather than a peer button, because it ends the session
 * and discards every capture taken so far.
 */
const BarcodeFailure = ({
  onExit,
  onRetry,
  onContinue,
  description,
  header,
  body,
}: Props) => {
  const { t } = useTranslation();
  return (
    <div className={commonStyles.failureContainer}>
      <div className={commonStyles.errorBadge}>
        <ErrorIcon
          sx={{
            color: "var(--color-warning)",
          }}
        />
        <div className={commonStyles.errorHeader}>{header}</div>
      </div>
      <CardContent>
        <span className={commonStyles.errorDescription}>{description}</span>
        <p className={commonStyles.failureDescription}>{body}</p>
        <div className={commonStyles.bottomFloatButtonContainer}>
          <button onClick={onRetry} className={commonStyles.button_primary}>
            {t('button.retake')}
          </button>
          <button onClick={onContinue} className={commonStyles.button_secondary}>
            {t('button.continue')}
          </button>
        </div>
        <p className={commonStyles.barcodeSwitchDevice}>
          <span
            role="button"
            tabIndex={0}
            className={commonStyles.link}
            onClick={onExit}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onExit();
              }
            }}
          >
            {t('barcode.switchtophone')}
          </span>
        </p>
      </CardContent>
    </div>
  );
};

export default BarcodeFailure;
